import { useEffect, useState } from "react";
import { Mail, Phone, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError, formatDate, LEAD_KINDS, LEAD_STATUS } from "@/lib/api";
import { ConfirmDelete } from "@/components/admin/ConfirmDelete";

const waNumber = (phone) => phone.replace(/[^\d]/g, "");

export const AdminLeads = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/leads", { params: filter === "all" ? {} : { status: filter } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (l, status) => {
    try { await api.patch(`/leads/${l.id}`, { status }); toast.success("Statut mis à jour"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (l) => {
    try { await api.delete(`/leads/${l.id}`); toast.success("Demande supprimée"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-leads">
      <div className="flex flex-wrap items-end justify-between gap-4 mt-2">
        <h1 className="font-display text-4xl">Demandes & contacts</h1>
        <div className="flex gap-2 flex-wrap">
          {[["all", "Toutes"], ...Object.entries(LEAD_STATUS).map(([k, s]) => [k, s.label])].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`leads-filter-${k}`}
              className={`px-4 py-2 rounded-full text-xs border transition-colors ${filter === k ? "bg-[#2F2A28] text-white border-[#2F2A28]" : "border-[#2F2A28]/15 hover:border-[#2F2A28]/40"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="mt-8 space-y-3">
        {items.length === 0 && <p className="p-6 bg-white border border-[#2F2A28]/10 text-sm text-[#756D69]" data-testid="leads-empty">Aucune demande.</p>}
        {items.map((l) => (
          <div key={l.id} className="bg-white border border-[#2F2A28]/10 rounded-sm p-5 md:p-6 grid md:grid-cols-[1fr_auto] gap-4" data-testid={`lead-row-${l.id}`}>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-medium text-lg">{l.name}</p>
                <span className="text-xs px-2.5 py-0.5 rounded-full bg-[#F2EDE4]">{LEAD_KINDS[l.kind]}</span>
                {l.programme_name && <span className="text-xs text-[#C88A58]">· {l.programme_name}</span>}
              </div>
              <p className="text-xs text-[#756D69] mt-1">{formatDate(l.created_at)}</p>
              {l.message && <p className="text-sm mt-3 text-[#4a4340] whitespace-pre-line">{l.message}</p>}
              <div className="flex flex-wrap gap-4 mt-4 text-sm">
                <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1.5 hover:text-[#C88A58]" data-testid={`lead-phone-${l.id}`}><Phone className="h-4 w-4" />{l.phone}</a>
                <a href={`https://wa.me/${waNumber(l.phone)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#1fb658]" data-testid={`lead-whatsapp-${l.id}`}><MessageCircle className="h-4 w-4" />WhatsApp</a>
                <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 hover:text-[#C88A58]" data-testid={`lead-email-${l.id}`}><Mail className="h-4 w-4" />{l.email}</a>
              </div>
            </div>
            <div className="flex md:flex-col items-center md:items-end gap-2">
              <Select value={l.status} onValueChange={(v) => setStatus(l, v)}>
                <SelectTrigger className={`w-36 h-9 ${LEAD_STATUS[l.status].cls}`} data-testid={`lead-status-select-${l.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(LEAD_STATUS).map(([k, s]) => <SelectItem key={k} value={k}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
              <ConfirmDelete title={`Supprimer la demande de ${l.name} ?`} onConfirm={() => remove(l)} testId={`lead-delete-${l.id}`} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
